import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {brandInsertT, brandT} from '@/lib/types/brand.types';
import {FormFieldT} from '@/lib/types/form.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderBrandTableStats} from '@/lib/config/brandTableConfig';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useBrands} from '@/providers/components/brandsProvider';

import {ConfirmDeleteModalComponent, ModalFormComponent, StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTableRpc} from '@/components/dataTableRpc';

const initialFormData: brandInsertT = {
  brand_address: '',
  brand_address2: '',
  brand_city: '',
  brand_description: '',
  brand_email: '',
  brand_license_number: '',
  brand_logo_url: '',
  brand_name: '',
  brand_phone: '',
  brand_postal_code: '',
  brand_state: '',
  created_by: '',
  date_created: '',
  date_modified: '',
};

const createFields: FormFieldT<brandT>[] = [
  {name: 'brand_name', label: 'Brand Name', type: 'text', placeholder: 'Brand Name', required: true},
  {
    name: 'brand_description',
    label: 'Brand Description',
    type: 'text',
    placeholder: 'Brand Description',
    required: true,
  },
  {name: 'brand_address', label: 'Address', type: 'text', placeholder: 'Address', required: true},
  {name: 'brand_address2', label: 'Address 2', type: 'text', placeholder: 'Address 2', required: false},
  {name: 'brand_city', label: 'City', type: 'text', placeholder: 'City', required: true},
  {name: 'brand_state', label: 'State', type: 'text', placeholder: 'State', required: true},
  {name: 'brand_postal_code', label: 'Postal Code', type: 'text', placeholder: 'Postal Code', required: true},
  {
    name: 'brand_license_number',
    label: 'License Number',
    type: 'text',
    placeholder: 'License Number',
    required: true,
  },
  {name: 'brand_email', label: 'Email', type: 'email', placeholder: 'Email', required: true},
  {name: 'brand_phone', label: 'Phone', type: 'text', placeholder: 'Phone', required: true},
  {name: 'brand_logo_url', label: 'Logo URL', type: 'text', placeholder: 'Logo URL', required: false},
];

const editFields: FormFieldT<brandT>[] = [...createFields];

function AdminBrands() {
  const {isAdministrator} = useAuth();

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Refs
  const selectedRetailerIdRef = useRef<number | null>(null);
  const formDataRef = useRef<brandInsertT>(initialFormData);

  const {createData, updateData, deleteData, getBrand} = useBrands();

  // Reactivity
  const {data: stats, refetch: refetchStats} = useViewQuery({
    view: 'brand_stats',
  });

  // Functions
  const handleCreate = async (data: brandInsertT) => {
    try {
      setIsSubmitting(true);
      await createData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to create retailer ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: brandT) => {
    if (selectedRetailerIdRef.current === null) {
      setSubmissionError('No retailer selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to update retailer: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedRetailerIdRef.current === null) {
      setDeleteError('No speculator selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await deleteData(selectedRetailerIdRef.current);
      refetchStats();
      setIsDeleteModalVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to delete user ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    if (!isAdministrator) return;
    formDataRef.current = initialFormData;
    setIsEditing(false);
    setIsVisible(true);
  };

  const handleEdit = async (brandId: number) => {
    const brand = await getBrand(brandId);
    formDataRef.current = brand;
    selectedRetailerIdRef.current = brand.brand_id;
    setIsEditing(true);
    setIsVisible(true);
  };

  const handleDeleteEvent = (brandId: number) => {
    selectedRetailerIdRef.current = brandId;
    setIsDeleteModalVisible(true);
  };

  // Stats data for stats component
  const statsRow: statsRowT = renderBrandTableStats(stats?.total, stats?.active, stats?.inactive);

  return (
    <Page currentPage="Brands">
      <StatsRowComponent stats={statsRow} />
      <DataTableRpc
        title="Brands Summary"
        functionName="get_brand_summary"
        columns={[
          {
            header: 'Logo',
            field: 'brand_logo_url',
            render: row => (
              <img src={row.brand_logo_url || '/images/logo/logo.png'} alt="Brand Logo" className="h-10 w-10" />
            ),
          },
          {
            header: 'Name',
            field: 'brand_name',
            render: row => (
              <Link className="text-primary hover:text-primary dark:text-primary" to={`/brands/${row.brand_id}`}>
                {row.brand_name}
              </Link>
            ),
          },
          {
            header: 'Description',
            field: 'brand_description',
          },
          {
            header: 'Products Count',
            field: 'total_products',
          },
          {
            header: 'Categories',
            field: 'unique_categories',
          },
          {
            header: 'Active',
            field: 'active_products',
          },
          {
            header: 'Inactive',
            field: 'inactive_products',
          },
        ]}
        searchColumn="brand_name"
        onAdd={handleAdd}
        actions={[
          {
            icon: 'ri-pencil-line',
            color: 'info',
            handler: row => handleEdit(row.brand_id),
            permission: () => isAdministrator,
          },
          {
            icon: 'ri-delete-bin-line',
            color: 'danger',
            handler: row => handleDeleteEvent(row.brand_id),
            permission: () => isAdministrator,
          },
        ]}
      />
      <ModalFormComponent
        isVisible={isVisible}
        fields={isEditing ? editFields : createFields}
        formData={formDataRef.current}
        onSubmit={(data: brandT) => {
          setSubmissionError(null); // Clear previous error before submitting
          if (isEditing) {
            handleUpdate(data);
          } else {
            handleCreate(data);
          }
        }}
        onClose={() => setIsVisible(false)}
        error={submissionError} // Pass the error to the modal
        isSubmitting={isSubmitting} // Pass the loading state to the modal
        title={isEditing ? 'Edit Brand' : 'Create New Brand'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this brand? This action cannot be undone."
        isSubmitting={isSubmitting}
        error={deleteError}
      />
    </Page>
  );
}

export default AdminBrands;
